import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import BackBar from "../components/BackBar";
import Brand from "../components/Brand";
import { toast } from "sonner";
import {
  HandHeart, CheckCircle2, Mail, Lock, BookOpen, Share2, Copy,
  Sparkles, Sun, Heart, Gift,
} from "lucide-react";

/* Public landing page → free "Praying For Intimacy" flipbook give-away.
   Captures email + name → auto-creates a free Faitharama account → emails
   the flipbook link. Reads ?ref=CODE from URL or localStorage so the
   referrer earns affiliate credit if/when this person upgrades later.

   URL: /free-gift/praying-for-intimacy?ref=BUILD-03KL
*/

const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";
const DARK = "#0F172A";

const ACTS = [
  { letter: "A", color: G,    label: "Adoration",     blurb: "How to start every prayer with the right heart." },
  { letter: "C", color: O,    label: "Confession",    blurb: "Why confession isn't punishment — it's intimacy." },
  { letter: "T", color: WINE, label: "Thanksgiving",  "blurb": "Five words that re-wire your gratitude." },
  { letter: "S", color: DARK, label: "Supplication",  blurb: "How to ask Him for what your real life needs." },
];

const COVER_URL = `${API}/uploads/flipbook_praying-for-intimacy_p0_9c9d9c50.png`;

export default function PrayerGiftLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [shareLink, setShareLink] = useState("");
  const formRef = useRef(null);

  // Resolve ref code: ?ref=... wins; else localStorage (set by RefCapture)
  const refCode = (params.get("ref") || localStorage.getItem("faitharama_ref") || "").trim();

  // Build a personal share link (no auth needed; uses ref of the current visitor if logged in)
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const code = r.data?.affiliate_code;
      if (code) setShareLink(`${window.location.origin}/free-gift/praying-for-intimacy?ref=${code}`);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email please.");
    setLoading(true);
    try {
      const r = await api.post("/leads/prayer-gift", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        ref_code: refCode,
      });
      const d = r.data || {};
      if (d.temp_password) setTempPw(d.temp_password);
      setDone(true);
      toast.success(d.new_member
        ? "★ Welcome to Faitharama! Opening your guide…"
        : "Sent! Opening your guide…");
      // Give them ~1.4s to read the success card, then jump to the flipbook
      setTimeout(() => navigate(d.flipbook_url || "/read/praying-for-intimacy"), 1400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyShare = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Share link copied!");
    } catch { toast.error("Couldn't copy — long-press the link instead."); }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="prayer-gift-landing">
      <SiteNav />
      <BackBar />

      {/* ═══════════ HERO ═══════════ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-6 pb-12 md:pb-16">
        <div className="grid md:grid-cols-[1fr_460px] gap-10 md:gap-14 items-center">
          {/* LEFT COL — copy + form */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-bold mb-4"
                 style={{ background: O, color: DARK }}>
              <Gift className="w-3.5 h-3.5"/> A Free Gift From Mike
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-red-wine leading-[1.05]" style={{ textWrap: "balance" }}>
              Stop guessing how to pray.<br/>
              <span style={{ color: G }}>Pray the way Jesus taught.</span>
            </h1>
            <p className="text-base sm:text-lg text-foreground/85 mt-5 max-w-xl" style={{ textWrap: "pretty" }}>
              A free 12-page flipbook apologetics. The four kinds of prayer Jesus modeled in His
              own Sermon on the Mount — <strong>A · C · T · S</strong>. With Scripture from the
              NKJV on every page and a worksheet you can fill in tonight.
            </p>

            {/* ACTS micro-list */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="acts-list">
              {ACTS.map((a) => (
                <div key={a.letter} className="flex items-start gap-3 p-3 rounded-lg bg-white border border-border/60 shadow-sm">
                  <div className="shrink-0 w-9 h-9 rounded-full text-white font-bold flex items-center justify-center font-serif-display text-lg"
                       style={{ background: a.color }}>
                    {a.letter}
                  </div>
                  <div>
                    <div className="font-bold text-foreground text-sm leading-tight">{a.label}</div>
                    <div className="text-xs text-foreground/65 mt-0.5">{a.blurb}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* FORM */}
            <form
              ref={formRef}
              onSubmit={submit}
              data-testid="prayer-gift-form"
              className="mt-7 rounded-2xl border-2 shadow-xl p-5 sm:p-6 bg-white"
              style={{ borderColor: O }}
            >
              {!done ? (
                <>
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>
                    <Lock className="w-3.5 h-3.5"/> Drop your name + email to open it
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <input
                      data-testid="prayer-gift-name"
                      required
                      placeholder="Your first name"
                      className="px-4 py-3 rounded-md border-2 border-border bg-white text-base focus:border-[#F39A1F] outline-none"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <input
                      data-testid="prayer-gift-email"
                      required
                      type="email"
                      placeholder="your@email.com"
                      className="px-4 py-3 rounded-md border-2 border-border bg-white text-base focus:border-[#F39A1F] outline-none"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="prayer-gift-submit"
                    className="w-full mt-4 inline-flex items-center justify-center gap-2 text-white font-bold uppercase tracking-[0.16em] text-sm rounded-md py-3.5 hover:opacity-90 transition disabled:opacity-60"
                    style={{ background: G }}
                  >
                    <Mail className="w-4 h-4" /> {loading ? "Sending…" : "Send Me The Guide"}
                  </button>
                  <div className="text-[11px] text-foreground/55 text-center mt-3">
                    No spam. Unsubscribe anytime.{" "}
                    {refCode && (
                      <span data-testid="prayer-gift-ref-tag" className="font-bold" style={{ color: G }}>
                        Referred by <code>{refCode}</code>.
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-foreground/65 text-center mt-2">
                    <strong>Bonus:</strong> You'll also get a free <Brand/> account so you can send a prayer request straight to Mike.
                  </div>
                </>
              ) : (
                <div data-testid="prayer-gift-success" className="text-center py-3">
                  <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: G }} />
                  <div className="font-serif-display text-2xl text-red-wine mt-3">
                    Thank you, {form.name.split(" ")[0]}.
                  </div>
                  <p className="text-sm text-foreground/80 mt-2">
                    Opening your flipbook now… {tempPw ? "Save your temp password below — we couldn't reach your inbox just now." : "Check your inbox too — we just emailed you the link."}
                  </p>
                  {tempPw && (
                    <div className="mt-4 rounded-lg p-3 text-left text-sm"
                         style={{ background: "#EAF6FC", border: `2px solid ${O}` }}>
                      <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>
                        ★ Your Free Account Login
                      </div>
                      <div className="mt-1.5 text-foreground">
                        Email: <strong>{form.email}</strong><br/>
                        Temp Password: <code className="px-2 py-0.5 rounded font-mono font-bold" style={{ background: "#fff", color: DARK, letterSpacing: "0.05em" }}>{tempPw}</code>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>

          {/* RIGHT COL — cover preview */}
          <div className="order-first md:order-last">
            <div
              className="rounded-2xl overflow-hidden shadow-2xl border-4 mx-auto max-w-[400px]"
              style={{ borderColor: O, background: "#0F172A" }}
            >
              <img
                src={COVER_URL}
                alt="Praying For Intimacy — Faitharama free guide cover"
                className="block w-full h-auto"
                data-testid="prayer-gift-cover"
              />
            </div>
            <div className="text-center mt-3 text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: G }}>
              ★ 12 Pages · NKJV · Free
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ WHAT'S INSIDE ═══════════ */}
      <section className="border-y-2" style={{ borderColor: "rgba(243,154,31,0.25)", background: "linear-gradient(180deg,#FFFFFF 0%,#FFFFFF 100%)" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>
              ★ What's Inside
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine mt-2" style={{ textWrap: "balance" }}>
              12 pages. One pattern. A deeper prayer life starting tonight.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { Icon: BookOpen, color: G,    title: "The Lord's Prayer · Matthew 6:9-13", body: "Jesus's blueprint — verse-by-verse. Why every line is a doorway to intimacy." },
              { Icon: Heart,    color: WINE, title: "The A·C·T·S Pattern",                body: "Adoration · Confession · Thanksgiving · Supplication. One page each, with NKJV." },
              { Icon: Sparkles, color: O,    title: "A Fill-In Worksheet",                body: "Four boxes. You write your own prayer. Then pray it out loud tonight." },
              { Icon: Sun,      color: G,    title: "Sample A·C·T·S Prayer",              body: "Stuck on words? A ready-made template you can read out loud and adapt." },
              { Icon: HandHeart,color: WINE, title: "3 Self-Reflection Questions",        body: "The honest questions Mike asks himself before going to bed each night." },
              { Icon: Mail,     color: O,    title: "Closing CTA",                        body: "Send Mike a real prayer request. He reads and prays over every one." },
            ].map(({ Icon, color, title, body }, i) => (
              <div key={i} className="rounded-xl bg-white border-2 border-border p-5 shadow-sm hover:shadow-md hover:translate-y-[-2px] transition-all" data-testid={`whats-inside-card-${i}`}>
                <Icon className="w-7 h-7 mb-3" style={{ color }} strokeWidth={1.75}/>
                <div className="font-bold text-foreground text-sm leading-tight">{title}</div>
                <div className="text-sm text-foreground/70 mt-1.5 leading-relaxed">{body}</div>
              </div>
            ))}
          </div>

          {/* Secondary CTA back to form */}
          <div className="text-center mt-10">
            <button
              onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
              data-testid="cta-scroll-to-form"
              className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-[0.16em] text-sm rounded-md py-3.5 px-8 hover:opacity-90 transition shadow-lg"
              style={{ background: G }}
            >
              <Mail className="w-4 h-4" /> Get My Free Guide
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════ SHARE STRIP — for logged-in members ═══════════ */}
      {shareLink && (
        <section className="max-w-5xl mx-auto px-5 md:px-8 py-10" data-testid="share-strip">
          <div className="rounded-2xl p-6 sm:p-8 text-white shadow-xl"
               style={{ background: "linear-gradient(135deg,#0F172A 0%,#1A2E1F 100%)", border: `2px solid ${O}` }}>
            <div className="flex flex-wrap items-center gap-4">
              <Share2 className="w-8 h-8 shrink-0" style={{ color: O }} />
              <div className="flex-1 min-w-[240px]">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: O }}>
                  ★ Your Share Link · Affiliate-Tracked
                </div>
                <div className="font-serif-display text-xl sm:text-2xl mt-1" style={{ textWrap: "balance" }}>
                  Share this gift. Earn a flat 50% on every friend who upgrades — same rate for everyone.
                </div>
                <code className="block mt-2 px-3 py-2 rounded bg-black/30 text-[12px] sm:text-sm break-all" data-testid="share-link-input">
                  {shareLink}
                </code>
              </div>
              <button
                onClick={copyShare}
                data-testid="share-copy-btn"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] font-bold px-5 py-3 rounded-md shrink-0"
                style={{ background: O, color: DARK }}
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ ATTRIBUTION FOOTER NOTE ═══════════ */}
      <div className="max-w-3xl mx-auto px-5 md:px-8 pt-2 pb-10 text-center text-xs text-foreground/55 italic" style={{ textWrap: "balance" }}>
        A·C·T·S framework adapted with gratitude from Brian S. Holmes, &ldquo;Driveway
        Discipleship&rdquo; Lesson 16 · MPowered Christian Ministries. Scripture: NKJV.
      </div>

      <SiteFooter />
    </div>
  );
}
